import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, type FeedCollection } from "@/api";
import { EmptyState, ErrorState } from "@/components/common/states";
import { IdeaOriginProvider } from "@/lib/idea-origin";
import { IdeaProductsPanel } from "@/pages/feed-collection";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { Skeleton } from "@osmix/ui/skeleton";

// Модалка «Идею в корзину» (кадр «Идею в корзину.svg»), открывается из
// избранного по клику на карточку идеи. Макет: контейнер 928×738 r20 #F8F8F8
// без рамки и тени, поля 16px по бокам и 20px сверху/снизу; слева фото идеи
// 525×698 r8, через 20px — колонка 351px с панелью «Товары из идеи» (та же,
// что на странице идеи, вариант modal). Затемнение — чёрный 20% + размытие
// (Figma blur 3 = CSS 1,5px). На узком экране колонки складываются
// в одну, и модалка прокручивается целиком.
export function IdeaCartModal(t0) {
  const $ = _c(17);
  const {
    collection,
    open,
    onOpenChange
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== collection?.title || $[1] !== t) {
    t1 = collection?.title ?? t("feed.productsInIdea", {
      defaultValue: "\u0422\u043E\u0432\u0430\u0440\u044B \u0438\u0437 \u0438\u0434\u0435\u0438"
    });
    $[0] = collection?.title;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <DialogTitle className="sr-only">{t1}</DialogTitle>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = collection?.description ?? "";
  let t4;
  if ($[5] !== t3) {
    t4 = <DialogDescription className="sr-only">{t3}</DialogDescription>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== collection) {
    t5 = collection && <IdeaCartBody key={collection.id} collection={collection} />;
    $[7] = collection;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t4 || $[11] !== t5) {
    t6 = <DialogContent showCloseButton={false} overlayClassName="bg-black/20 backdrop-blur-[1.5px]" className="flex max-h-[calc(100dvh-2rem)] w-[95vw] max-w-[928px] flex-col gap-5 overflow-y-auto rounded-[20px] border-0 bg-[#F8F8F8] p-4 shadow-none sm:max-w-[928px] lg:h-[738px] lg:flex-row lg:overflow-hidden lg:px-4 lg:py-5">{t2}{t4}{t5}</DialogContent>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== onOpenChange || $[14] !== open || $[15] !== t6) {
    t7 = <Dialog open={open} onOpenChange={onOpenChange}>{t6}</Dialog>;
    $[13] = onOpenChange;
    $[14] = open;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function IdeaCartBody(t0) {
  const $ = _c(24);
  const {
    collection: c
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== c.id) {
    t1 = ["products", "byIds", c.id];
    $[0] = c.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== c.productIds) {
    t2 = () => api.products.byIds(c.productIds);
    $[2] = c.productIds;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const products = useQuery(t3);
  let t4;
  if ($[7] !== t) {
    t4 = t("common.close", {
      defaultValue: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C"
    });
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <X className="size-6" aria-hidden="true" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4) {
    t6 = <DialogClose aria-label={t4} className="flex size-6 shrink-0 items-center justify-center rounded-[4px] text-osmix-text-2 outline-none transition-colors hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">{t5}</DialogClose>;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const close = t6;
  let t7;
  if ($[12] !== c.cover || $[13] !== c.title) {
    t7 = <img src={c.cover} alt={c.title} className="aspect-[526/698] max-h-[55dvh] w-full shrink-0 rounded-[8px] bg-osmix-gray-100 object-cover lg:aspect-auto lg:h-full lg:max-h-none lg:w-auto lg:min-w-0 lg:flex-1" />;
    $[12] = c.cover;
    $[13] = c.title;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== c.id || $[16] !== c.slug || $[17] !== close || $[18] !== products || $[19] !== t) {
    t8 = <div className="flex shrink-0 flex-col lg:min-h-0 lg:w-[351px] lg:shrink-0">{products.isPending ? <div className="flex flex-col gap-5"><div className="flex h-6 items-center justify-between"><Skeleton className="h-5 w-40 rounded-[4px]" />{close}</div>{Array.from({
          length: 3
        }, _temp)}</div> : products.error || !products.data || products.data.length === 0 ? <div className="flex flex-col gap-5"><div className="flex h-6 items-center justify-end">{close}</div>{products.error ? <ErrorState onRetry={() => products.refetch()} /> : <EmptyState title={t("catalog.empty")} />}</div> : <IdeaOriginProvider value={{
        collectionId: c.id,
        collectionSlug: c.slug
      }}><IdeaProductsPanel products={products.data} variant="modal" headerAction={close} /></IdeaOriginProvider>}</div>;
    $[15] = c.id;
    $[16] = c.slug;
    $[17] = close;
    $[18] = products;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7 || $[22] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
function _temp(_, i) {
  return <Skeleton key={i} className="h-[139px] w-full rounded-[20px] bg-white" />;
}